import React, { useEffect, useMemo, useState } from "react"
import { motion } from "framer-motion"
import { ChevronDown, ChevronUp } from "lucide-react"
import ModalOverlay from "../../UI/ModalOverlay/ModalOverlay.tsx"
import CustomSelect from "../../../pages/ProjectDocumentation/components/CustomSelect.tsx"
import { useDebouncedCatalogMaterialSearch } from "../../../hooks/useDebouncedCatalogMaterialSearch.ts"
import {
  batchCatalogMaterialsByIds,
  searchItemToCatalogMaterial,
} from "../../../services/catalogMaterialsService.ts"
import type { CatalogMaterial, CategoriesResponse, SelectedMaterial } from "../../../types/calculadoraDeMateriales.ts"
import { isMaterialOutOfCatalog } from "../../../types/calculadoraDeMateriales.ts"
import { MATERIAL_UNIT_SELECT_OPTIONS } from "../../../utils/marketplace/materialUnitSelectOptions.ts"
import popupStyles from "./AddCustomMaterialPopup.module.scss"
import editStyles from "./CalculadoraEditMaterialModal.module.scss"

type CatalogRow = CatalogMaterial & { category: string; categoryId: string }

export type CalculadoraEditMaterialModalProps = {
  isOpen: boolean
  onClose: () => void
  initialMaterial: SelectedMaterial
  categories: CategoriesResponse
  isEstimacionDeCostos: boolean
  onSave: (material: SelectedMaterial) => void
}

const variants = {
  hidden: { opacity: 0, scale: 0.92 },
  visible: { opacity: 1, scale: 1 },
}

const CalculadoraEditMaterialModal: React.FC<CalculadoraEditMaterialModalProps> = ({
  isOpen,
  onClose,
  initialMaterial,
  categories,
  isEstimacionDeCostos,
  onSave,
}) => {
  const [linkedCatalog, setLinkedCatalog] = useState<CatalogRow | null>(null)
  const [nameInput, setNameInput] = useState("")
  const [quantity, setQuantity] = useState(1)
  const [categoryId, setCategoryId] = useState("")
  const [unit, setUnit] = useState("")
  const [price, setPrice] = useState(0)

  const { items: searchItems, status: searchStatus } = useDebouncedCatalogMaterialSearch(nameInput, {
    enabled: isOpen && !linkedCatalog && nameInput.trim().length >= 2,
    limit: 40,
    categoryId: categoryId || undefined,
  })

  useEffect(() => {
    if (!isOpen) return
    setQuantity(Math.max(1, Number(initialMaterial.quantity) || 1))
    const inCat = !isMaterialOutOfCatalog(initialMaterial)
    if (inCat && initialMaterial.id != null) {
      void (async () => {
        const batch = await batchCatalogMaterialsByIds([initialMaterial.id])
        const hit = batch[0]
        if (hit) {
          const row = searchItemToCatalogMaterial(hit)
          setLinkedCatalog(row)
          setNameInput(String(initialMaterial.name ?? row.name).trim() || row.name)
          setCategoryId(row.categoryId)
          setUnit(row.unit ?? "u")
        } else {
          setLinkedCatalog(null)
          setNameInput(String(initialMaterial.name ?? "").trim())
          setCategoryId(
            initialMaterial.categoryId != null && initialMaterial.categoryId !== ""
              ? String(initialMaterial.categoryId)
              : ""
          )
          setUnit(initialMaterial.unit ?? "")
        }
      })()
    } else {
      setLinkedCatalog(null)
      setNameInput(String(initialMaterial.name ?? "").trim())
      setCategoryId(
        initialMaterial.categoryId != null && initialMaterial.categoryId !== ""
          ? String(initialMaterial.categoryId)
          : ""
      )
      setUnit(initialMaterial.unit ?? "")
    }
    setPrice(
      initialMaterial.price != null && Number.isFinite(Number(initialMaterial.price))
        ? Number(initialMaterial.price)
        : 0
    )
  }, [isOpen, initialMaterial])

  const suggestionRows = useMemo(() => {
    if (linkedCatalog || nameInput.trim().length < 2 || searchStatus !== "ready") return []
    return searchItems.map((item) => searchItemToCatalogMaterial(item))
  }, [linkedCatalog, nameInput, searchItems, searchStatus])

  const categoryOptions = useMemo(
    () =>
      (categories?.data || []).map((category) => ({
        value: String(category.id),
        label: category.name,
      })),
    [categories?.data]
  )

  const applySuggestion = (row: CatalogRow) => {
    setLinkedCatalog(row)
    setNameInput(row.name)
    setCategoryId(row.categoryId)
    setUnit(row.unit ?? "u")
    if (row.price != null && Number.isFinite(Number(row.price))) {
      setPrice(Number(row.price))
    }
  }

  const handleNameChange = (v: string) => {
    setNameInput(v)
    if (linkedCatalog && v.trim() !== linkedCatalog.name.trim()) {
      setLinkedCatalog(null)
    }
  }

  const handleCategoryChange = (v: string) => {
    setCategoryId(v)
    if (linkedCatalog && v !== linkedCatalog.categoryId) {
      setLinkedCatalog(null)
    }
  }

  const handleUnitChange = (v: string) => {
    setUnit(v)
    if (linkedCatalog && v !== (linkedCatalog.unit ?? "u")) {
      setLinkedCatalog(null)
    }
  }

  const handleQuantityDelta = (delta: number) => {
    setQuantity((q) => Math.max(1, q + delta))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const rawKeep = initialMaterial.rawName?.trim() ? initialMaterial.rawName : undefined
    if (linkedCatalog) {
      const next: SelectedMaterial = {
        id: linkedCatalog.id,
        name: linkedCatalog.name,
        unit: linkedCatalog.unit ?? "u",
        quantity,
        category: linkedCatalog.category,
        categoryId: linkedCatalog.categoryId,
        temporary: false,
        inCatalog: true,
        rawName: rawKeep,
      }
      if (isEstimacionDeCostos) {
        next.price = Number(price) >= 0 ? Number(price) : 0
      } else if (linkedCatalog.price != null && Number.isFinite(Number(linkedCatalog.price))) {
        next.price = Number(linkedCatalog.price)
      }
      onSave(next)
      onClose()
      return
    }
    if (!categoryId || !nameInput.trim() || !unit) return
    const cat = categories.data?.find((c) => String(c.id) === String(categoryId))
    const next: SelectedMaterial = {
      id:
        initialMaterial.temporary === true ||
        (typeof initialMaterial.id === "string" && initialMaterial.id.startsWith("autofill-temp"))
          ? initialMaterial.id
          : `custom-${Date.now()}`,
      name: nameInput.trim(),
      unit,
      quantity,
      category: cat?.name ?? "Sin Categoría",
      categoryId,
      temporary: true,
      inCatalog: false,
      rawName: rawKeep,
    }
    if (isEstimacionDeCostos) {
      next.price = Number(price) >= 0 ? Number(price) : 0
    }
    onSave(next)
    onClose()
  }

  const canSubmit = linkedCatalog
    ? quantity >= 1
    : Boolean(
        categoryId &&
          nameInput.trim() &&
          unit &&
          quantity >= 1 &&
          (!isEstimacionDeCostos || Number(price) >= 0)
      )

  if (!isOpen) return null

  return (
    <ModalOverlay isOpen onClose={onClose} preventClose zIndex={34}>
      <motion.div initial="hidden" animate="visible" variants={variants} className={popupStyles.popup}>
        <div className={popupStyles.popup_header}>
          <div>
            <h2 className={popupStyles.popup_header_title}>Editar material</h2>
            <p className={popupStyles.popup_header_subtitle}>Nombre, clasificación y cantidad.</p>
          </div>
          <button type="button" onClick={onClose} className={popupStyles.popup_header_cancelBtn}>
            Cancelar
          </button>
        </div>
        <form className={popupStyles.popup_form} onSubmit={handleSubmit}>
          <div className={popupStyles.popup_formBody}>
            <div className={popupStyles.formSection}>
              <h3 className={popupStyles.formSectionTitle}>Nombre</h3>
              <div className={popupStyles.formGroup}>
                <label htmlFor="editMat-name">Nombre del material</label>
                <input
                  id="editMat-name"
                  name="name"
                  type="text"
                  value={nameInput}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="Escribí para filtrar el catálogo o el nombre libre"
                  autoComplete="off"
                  required
                />
              </div>
              {nameInput.trim() && !linkedCatalog && suggestionRows.length > 0 ? (
                <ul className={editStyles.catalogList} role="listbox" aria-label="Catálogo">
                  {suggestionRows.map((row) => {
                    return (
                      <button
                        key={`${row.categoryId}-${row.id}`}
                        type="button"
                        className={editStyles.catalogListItem}
                        onClick={() => applySuggestion(row)}
                      >
                        <span className={editStyles.catalogListItemName}>{row.name}</span>
                        <span className={editStyles.catalogListItemMeta}>
                          {row.category}
                          {row.unit ? ` · ${row.unit}` : ""}
                        </span>
                      </button>
                    )
                  })}
                </ul>
              ) : null}
            </div>

            <div className={popupStyles.formSection}>
              <h3 className={popupStyles.formSectionTitle}>Clasificación</h3>
              <div className={popupStyles.formGroup}>
                <label htmlFor="editMat-categoryId">Categoría</label>
                <CustomSelect
                  value={categoryId}
                  onChange={(v: string) => handleCategoryChange(v)}
                  options={categoryOptions}
                  placeholder="Seleccioná una categoría"
                />
              </div>
              <div className={popupStyles.formGroup}>
                <label htmlFor="editMat-unit">Unidad</label>
                <CustomSelect
                  value={unit}
                  onChange={(v: string) => handleUnitChange(v)}
                  options={MATERIAL_UNIT_SELECT_OPTIONS}
                  placeholder="Unidad"
                />
              </div>
              {isEstimacionDeCostos ? (
                <div className={popupStyles.formGroup}>
                  <label htmlFor="editMat-price">Precio unitario estimado</label>
                  <input
                    id="editMat-price"
                    name="price"
                    type="number"
                    min={0}
                    value={price}
                    onChange={(e) => setPrice(Number(e.target.value))}
                    placeholder="0"
                    required
                  />
                </div>
              ) : null}
            </div>

            <div className={popupStyles.formSection}>
              <h3 className={popupStyles.formSectionTitle}>Cantidad</h3>
              <div className={popupStyles.quantityControl}>
                <button type="button" onClick={() => handleQuantityDelta(1)} className={popupStyles.quantityButton}>
                  <ChevronUp size={18} />
                </button>
                <input
                  type="number"
                  min={1}
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, Number.parseInt(e.target.value, 10) || 1))}
                />
                <button type="button" onClick={() => handleQuantityDelta(-1)} className={popupStyles.quantityButton}>
                  <ChevronDown size={18} />
                </button>
              </div>
              </div>
            </div>

          <div className={popupStyles.buttonGroup}>
            <button disabled={!canSubmit} type="submit" className={popupStyles.buttonGroup_acceptBtn}>
              Guardar cambios
            </button>
          </div>
        </form>
      </motion.div>
    </ModalOverlay>
  )
}

export default CalculadoraEditMaterialModal
